import { useEffect, useState } from 'react';
import { Box, Center, Stack, Text } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { Button } from 'src/components';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { AuthCard } from './AuthCard';

type SuccessMessageVariant = 'settings' | 'recovery' | 'invite' | 'sso_linked';

interface SuccessMessageProps {
  variant: SuccessMessageVariant;
  isLoading?: boolean;
  onAction?: () => Promise<void> | void;
}

const VARIANT_CONFIG: Record<
  SuccessMessageVariant,
  {
    title: string;
    description: string;
    buttonText?: string;
    showCountdown?: boolean;
    countdownSeconds?: number;
  }
> = {
  settings: {
    title: 'Settings updated',
    description: 'Your changes have been saved. You can close this tab.',
  },
  recovery: {
    title: 'Password updated',
    description: 'Your password has been updated successfully.',
    buttonText: 'Go to Login',
  },
  invite: {
    title: 'Account created!',
    description: 'Your account has been created successfully.',
    buttonText: 'Continue to login',
    showCountdown: true,
    countdownSeconds: 3,
  },
  sso_linked: {
    title: 'Account linked',
    description:
      'Your SSO account has been linked. Please log in with your new account to continue.',
    buttonText: 'Go to Login',
    showCountdown: true,
    countdownSeconds: 3,
  },
};

export const SuccessMessage = ({ variant, isLoading = false, onAction }: SuccessMessageProps) => {
  const config = VARIANT_CONFIG[variant];
  const [countdown, setCountdown] = useState(config.countdownSeconds ?? 0);
  const [isActionInProgress, setIsActionInProgress] = useState(false);

  useEffect(() => {
    if (!config.showCountdown || !onAction) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [config.showCountdown, onAction]);

  useEffect(() => {
    if (config.showCountdown && countdown === 0 && onAction && !isActionInProgress) {
      handleAction();
    }
  }, [countdown, config.showCountdown, onAction, isActionInProgress]);

  const handleAction = async () => {
    if (!onAction || isActionInProgress) return;
    setIsActionInProgress(true);
    await onAction();
  };

  const buttonLoading = isLoading || isActionInProgress;
  const buttonText =
    config.showCountdown && countdown > 0
      ? `${config.buttonText} (${countdown})`
      : config.buttonText;

  return (
    <AuthCard>
      <Stack gap="md" align="center">
        <Center w={64} h={64} bg="green.1" style={{ borderRadius: '50%' }}>
          <IconCheck size={32} color="var(--mantine-color-green-6)" />
        </Center>

        <Text fz="xl" fw={FONT_WEIGHTS.semibold} ta="center">
          {config.title}
        </Text>

        <Text size="sm" c="gray.6" ta="center">
          {config.description}
        </Text>

        {config.buttonText && onAction && (
          <Box w="100%" mt="xs">
            <Button onClick={handleAction} fullWidth loading={buttonLoading} variant="primary">
              {buttonText}
            </Button>
          </Box>
        )}
      </Stack>
    </AuthCard>
  );
};
